/**
 * Auth context provider for managing authentication state across the app.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useAuthState, type UseAuthReturn } from '@/hooks/useAuth';

const AuthContext = createContext<UseAuthReturn | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps the app and provides auth state.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const auth = useAuthState();

  // Check auth status on mount
  useEffect(() => {
    auth.refreshAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context. Must be used within AuthProvider.
 */
export function useAuth(): UseAuthReturn {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
